import { ButtonLink } from '@app/components/base/ButtonLink';
import { UserDisplay } from '@app/components/users';
import { ConnectionDisplayName } from '@app/modules/social-export/components/ConnectionDisplayName';
import { SocialPlatform } from '@app/modules/social-export/domain/platforms';
import {
  SocialExportGetConnectionStateDto,
  socialExportGetConnectionByIdQueryKey,
} from '@mi-front/api-v2';
import { Settings } from '@mui/icons-material';
import { Alert, Avatar, Box, Divider, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import { connectionsManageMessages as messages } from './connections-manage-messages';
import { ConnectionStationChip } from './ConnectionStationChip';
import { PlatformDisconnect } from './PlatformDisconnect';
import { useConnectionStations } from './useConnectionStations';

type Props = {
  platform: SocialPlatform;
  connection: SocialExportGetConnectionStateDto;
};

/**
 * Detail panel for the selected connection: linked stations (read-only) and configure/disconnect.
 */
export function ConnectionDetailPanel({ platform, connection }: Props) {
  const queryClient = useQueryClient();
  const { profile } = connection;
  const { linkedStations, stationIds } = useConnectionStations(connection.stationIds);

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        height: '100%',
        minHeight: 280,
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar src={profile.photo || undefined} sx={{ width: 24, height: 24 }}>
            {(connection.name || profile.displayName)?.charAt(0) || '?'}
          </Avatar>
          <ConnectionDisplayName connection={connection} variant="h6" />
        </Box>
        {connection.connectedBy ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            <FormattedMessage {...messages.connectedByPrefix} />{' '}
            <Box component="strong" sx={{ display: 'inline' }}>
              <UserDisplay showIcon id={connection.connectedBy} />
            </Box>
          </Typography>
        ) : null}
      </Box>

      <Box>
        {linkedStations.length > 0 ? (
          <Typography variant="body2" sx={{ mb: 1.25 }}>
            <FormattedMessage {...messages.stationsPublishingWithAccount} />
          </Typography>
        ) : null}
        {linkedStations.length > 0 ? (
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} alignItems="center">
            {linkedStations.map((station) => (
              <ConnectionStationChip key={station.id} station={station} />
            ))}
          </Stack>
        ) : (
          <Alert severity="warning" sx={{ py: 0.5, typography: 'body2', width: 'fit-content' }}>
            <FormattedMessage {...messages.noLinkedStations} />
          </Alert>
        )}
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <ButtonLink
            variant="outlined"
            color="info"
            size="small"
            to={`/admin/social-export/${platform.id.toLowerCase()}/settings/${connection.id}`}
            startIcon={<Settings />}
            onClick={() => {
              // Seed get-by-id cache so settings title shows the account name immediately.
              queryClient.setQueryData(
                socialExportGetConnectionByIdQueryKey(connection.id),
                connection,
              );
            }}
          >
            <FormattedMessage {...messages.configure} />
          </ButtonLink>
          <PlatformDisconnect
            connectionId={connection.id}
            platform={platform}
            linkedStationsCount={stationIds.length}
          />
        </Box>
      </Box>
    </Box>
  );
}
